import { i18n } from '@/plugins/i18n';
import router from '@/router';
import { showErrorSnackbar, showSnackbar } from '@/util/snackBarUtil';
import axios from 'axios';
import type { AxiosRequestConfig } from 'axios';
import { TokenService } from './TokenService';
import RefreshService from './RefreshService';
import AuthApi from './auth/AuthApi';
import {
  clearUserManagerAndSession,
  getSwitchedAccount,
  oAuthEnabled
} from './OidcService';

export interface ApiResponse<T> {
  members: T;
  total: number;
  count: number;
  start: number;
}

const baseURL = import.meta.env.VITE_API_BASE_URL as string;
let refreshPromise: Promise<unknown> | null;

const checkIfAccessTokenIsExpiring = () => {
  if (oAuthEnabled()) return false;
  const tokenExpirationInMilliSeconds = Number(
    TokenService.getAccessTokenExpiration()
  );
  const nowInMilliSeconds = Date.now();
  const minutesLeftUntilExpiration =
    (tokenExpirationInMilliSeconds - nowInMilliSeconds) / (60 * 1000);
  return minutesLeftUntilExpiration < 1;
};

const resendOriginalRequestWithNewToken = async (error: any) => {
  try {
    if (!refreshPromise) {
      refreshPromise = RefreshService.getNewAccessToken();
    }
    return await refreshPromise.then(async () => {
      // Resend the original request with updated token
      const updatedAccessToken = await TokenService.getAccessToken();
      const originalConfig = error.config as AxiosRequestConfig;
      if (originalConfig && originalConfig.headers && updatedAccessToken) {
        originalConfig.headers.Authorization = `Bearer ${updatedAccessToken}`;
        return axios.request(originalConfig).then((response) => {
          refreshPromise = null;
          return response;
        });
      }
    });
  } catch {
    // Resending failed again, log out
    refreshPromise = null;
    await AuthApi.cleanUpAndRedirectToLogin(true);
    return Promise.reject(error);
  }
};

const axiosInstance = axios.create({
  baseURL: baseURL
});

/**
 * Axios request interceptor to handle access token expiration and refresh.
 * The first two if cases cover two special cases when no access token is required
 * If there is no refresh token, the request is aborted.
 * If the access token is expired, it automatically refreshes the token and retries the request.
 * If refreshing fails, the request is aborted.
 */
axiosInstance.interceptors.request.use(async (req) => {
  // set additional Headers for switched account
  if (getSwitchedAccount()) {
    req.headers['Irm-Account-Id'] = getSwitchedAccount()?.accountId;
    req.headers['Irm-Namespace'] = getSwitchedAccount()?.namespace;
  }

  const accessToken = await TokenService.getAccessToken();
  const refreshToken = await TokenService.getRefreshToken();

  // Auth header is specified by the sender (AuthApi)
  if (req.auth) {
    return req;
  }

  // NO Auth. Special case for 'getUserByResetPasswordCode' and 'SoftwareVersionApi.ts'
  else if (req.headers.Authorization === '') {
    delete req.headers.Authorization;
    return req;
  }
  // no refresh token found, abort request and clean up
  else if (!refreshToken) {
    await AuthApi.cleanUpAndRedirectToLogin();
    const controller = new AbortController();
    req.signal = controller.signal;
    controller.abort();
    return req;
  }
  // has refresh and access token, as well as access token is not expiring
  else if (accessToken && !checkIfAccessTokenIsExpiring()) {
    req.headers.Authorization = `Bearer ${accessToken}`;
    return req;
  }
  // needs to refresh access token (legacy flow, no manual refresh in oAuthMode)
  else {
    if (!refreshPromise) {
      refreshPromise = RefreshService.getNewAccessToken();
    }
    await refreshPromise
      .then(async () => {
        const updatedAccessToken = await TokenService.getAccessToken();
        // Return the original request with updated token
        req.headers.Authorization = `Bearer ${updatedAccessToken}`;
      })
      .catch(() => {
        // Abort the request, when refreshing failed
        const controller = new AbortController();
        req.signal = controller.signal;
        controller.abort();
      })
      .finally(() => (refreshPromise = null));
    return req;
  }
});

axiosInstance.interceptors.response.use(
  (response) => {
    // Reset to baseURL for next request, in case it was changed to an individualURL
    axiosInstance.defaults.baseURL = baseURL;
    return response;
  },
  async (error) => {
    if (axios.isCancel(error)) {
      // Nothing to do. RefreshService already clears and logs out, if refreshing failed
    }
    switch (error.response.status) {
      case 400:
        // caller should handle this, should use function "showApiErrorSnackbar"
        break;
      case 401:
        // for login; only happens if the token validity time span in the BE is shorter than in the FE
        // 3rd party APIs should generally not log out the user (e.g. geocoding)
        if (
          error.config.baseURL.includes('login') ||
          error.config.baseURL.includes('geoapify') ||
          error.config.baseURL.includes('authorize') ||
          error.config.baseURL.includes('2fa/enforced/confirm')
        ) {
          throw error;
        } else {
          if (oAuthEnabled()) {
            await clearUserManagerAndSession();
            await AuthApi.cleanUpAndRedirectToLogin(true);
          } else {
            return resendOriginalRequestWithNewToken(error);
          }
          break;
        }
      case 403:
        showSnackbar(i18n.t('errors.permissionDenied').toString());
        break;
      case 404:
        // caller should handle this, should use function "showApiErrorSnackbar"
        break;
      case 406:
        // special confirmation error
        break;
      case 409:
        // special download error
        break;
      case 422:
        // unprocessable content
        break;
      case 500:
        await router.push('/500');
        break;
      default:
        showErrorSnackbar(
          i18n
            .t('errors.unknownError', {
              errorCode: `-${error.response.status}`
            })
            .toString()
        );
        break;
    }
    return Promise.reject(error);
  }
);

// individualURL is used in AuthApi for login and signup
const ApiService = {
  get: <TResponse = any>(
    resource: string,
    config = {} as AxiosRequestConfig,
    individualURL?: string
  ) => {
    if (individualURL) {
      axiosInstance.defaults.baseURL = individualURL;
    } else {
      axiosInstance.defaults.baseURL = baseURL;
    }
    return axiosInstance.get<TResponse>(resource, config);
  },

  post: <TResponse = any, TPayload = any>(
    resource: string,
    data?: TPayload,
    config = {} as AxiosRequestConfig,
    individualURL?: string
  ) => {
    if (individualURL) {
      axiosInstance.defaults.baseURL = individualURL;
    }
    return axiosInstance.post<TResponse>(resource, data, config);
  },

  put: <TResponse = any, TPayload = any>(
    resource: string,
    data?: TPayload,
    config = {} as AxiosRequestConfig,
    individualURL?: string
  ) => {
    if (individualURL) {
      axiosInstance.defaults.baseURL = individualURL;
    }
    return axiosInstance.put<TResponse>(resource, data, config);
  },

  patch: <TResponse = any, TPayload = any>(resource: string, data?: TPayload) =>
    axiosInstance.patch<TResponse>(resource, data),

  delete: (resource: string) => axiosInstance.delete(resource)
};

export default ApiService;
